import { EquipmentTypeDao, InfoTypeDao } from '@rock/db-admin';
import {
    type EquipmentType,
    type EquipmentTypeId,
    type InfoType,
    type InfoTypeId,
    isOfferableType,
    type RefreshId,
    type SpaceId,
} from '@rock/types';
import { shallowEqual } from '@rock/util';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';

const equipmentTypeDao = new EquipmentTypeDao();
const infoTypeDao = new InfoTypeDao();

/**
 * The catalog is one top-level collection — a single subscription serves every space's table view.
 * Exported for the pickers that must span spaces: a type's `spaceId` groups it, it does not fence it
 * (document coverage legitimately reaches types in another space — see `EquipmentTypeMultiSelect`).
 */
export const allEquipmentTypesState = equipmentTypeDao.equipmentTypesAtom();

/** Retirement is live-only, so this reads the live catalog, never a draft overlay. */
export const retiredEquipmentTypeIdsState = atom(async (get) => {
    const all = await get(allEquipmentTypesState);
    return new Set(all.filter((type) => !isOfferableType(type)).map((type) => type.id));
});

/** Equipment-types for a space — a derived filter on the `spaceId` grouping field. */
export const equipmentTypesState = atomFamily(
    (spaceId: SpaceId) =>
        atom(async (get) => {
            const all = await get(allEquipmentTypesState);
            return all.filter((t) => t.spaceId === spaceId);
        }),
    shallowEqual,
);
// The info-type catalog is one top-level collection too — a single subscription serves every view.
export const allInfoTypesState = infoTypeDao.infoTypesAtom();

/** Info-types for a space — a derived filter on the `spaceId` grouping field. */
export const infoTypesState = atomFamily(
    (spaceId: SpaceId) =>
        atom(async (get) => {
            const all = await get(allInfoTypesState);
            return all.filter((t) => t.spaceId === spaceId);
        }),
    shallowEqual,
);

export const equipmentTypeState = atomFamily(
    (typeId: EquipmentTypeId) => equipmentTypeDao.equipmentTypeAtom(typeId),
    shallowEqual,
);

// --- Staged editing (draft → preview → live) ---

/** All staged drafts (the `_draft/db` mirror collection — exactly the work in flight). */
const allEquipmentTypeDraftsState = equipmentTypeDao.equipmentTypeDraftsAtom();

/**
 * Admin list source: every live type overlaid with its draft (draft wins by id; draft-only types
 * appended) so newly-created and edited-but-unpublished types are visible in the table with a badge.
 * Un-scoped, because the catalog list spans spaces — it filters by the `spaceId` pill itself.
 */
export const allEquipmentTypesWithDraftsState = atom(async (get) => {
    const [live, drafts] = await Promise.all([get(allEquipmentTypesState), get(allEquipmentTypeDraftsState)]);
    const byId = new Map<EquipmentTypeId, EquipmentType>(live.map((t) => [t.id, t]));
    for (const d of drafts) byId.set(d.id, d);
    return [...byId.values()];
});

/**
 * Writable edit atom for the staging editors: reads the draft if one exists (else live), and writes
 * always go to the draft — through the DAO's `setEquipmentTypeDraft` (the DraftLayer choke point
 * that stamps `_draft: true`), never by setting the doc atom directly. So opening an editor shows
 * the work-in-progress and saving stages it without touching the live doc the app reads.
 */
export const equipmentTypeEditState = atomFamily((typeId: EquipmentTypeId) => {
    const draftAtom = equipmentTypeDao.equipmentTypeDraftAtom(typeId);
    const liveAtom = equipmentTypeDao.equipmentTypeAtom(typeId);
    return atom(
        async (get) => (await get(draftAtom)) ?? (await get(liveAtom)),
        async (_get, _set, value: EquipmentType) => {
            await equipmentTypeDao.setEquipmentTypeDraft(typeId, value);
        },
    );
}, shallowEqual);

/** Reactive draft/publish status for one type (drives the "unpublished changes" badge + Publish gating). */
export const equipmentTypeDraftStatusState = atomFamily(
    (typeId: EquipmentTypeId) => equipmentTypeDao.equipmentTypeDraftStatusAtom(typeId),
    shallowEqual,
);
export const infoTypeState = atomFamily((typeId: InfoTypeId) => infoTypeDao.infoTypeAtom(typeId), shallowEqual);

export const equipmentTypesByIdState = atomFamily(
    (spaceId: SpaceId) =>
        atom(async (get) => {
            const types = await get(equipmentTypesState(spaceId));
            const acc: Record<EquipmentTypeId, EquipmentType> = {};
            for (const t of types) {
                acc[t.id] = t;
            }
            return acc;
        }),
    shallowEqual,
);

export const infoTypesByIdState = atomFamily(
    (spaceId: SpaceId) =>
        atom(async (get) => {
            const types = await get(infoTypesState(spaceId));
            const acc: Record<InfoTypeId, InfoType> = {};
            for (const t of types) {
                acc[t.id] = t;
            }
            return acc;
        }),
    shallowEqual,
);

export const equipmentTypeRefreshState = atomFamily(
    ({ typeId, refreshId }: { typeId: EquipmentTypeId; refreshId?: RefreshId }) =>
        equipmentTypeDao.equipmentTypeRefreshAtom(typeId, refreshId),
    shallowEqual,
);
